<template>
  <div class="card mt-3 shadow-sm border-0">
    <div class="card-body p-0">
      <div v-if="loading" class="text-center p-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="text-muted mt-2">Buscando tickets...</p>
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm cs-table mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-3">Data</th>
              <th>Consultor</th>
              <th>Cliente</th>
              <th>Atividade</th>
              <th>Classificação</th>
              <th>Fixo</th>
              <th>Valor</th>
              <th>Movidesk</th>
              <th>Observação</th>
              <th>Status</th>
              <th class="text-end pe-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="tickets.length === 0">
              <td colspan="11" class="text-center text-muted py-5">
                <i class="fas fa-inbox fa-2x mb-2 d-block"></i>
                Nenhum ticket encontrado.
              </td>
            </tr>
            <tr v-for="ticket in tickets" :key="ticket.id">
              <td class="ps-3 text-nowrap">{{ formatDateBr(ticket.created_at) }}</td>
              <td>{{ ticket.user_name }}</td>
              <td>{{ ticket.customer_name }}</td>
              <td>{{ ticket.task_name }}</td>
              <td>{{ ticket.task_type_name }}</td>
              <td>
                <span :class="ticket.is_fixed_value ? 'text-success' : 'text-muted'">
                  {{ ticket.is_fixed_value ? 'Sim' : 'Não' }}
                </span>
              </td>
              <td class="text-nowrap">{{ ticket.is_fixed_value ? 'R$ ' + ticket.fixed_value : '-' }}</td>
              <td>
                <code v-if="ticket.movidesk_ticket_id" class="text-dark">#{{ ticket.movidesk_ticket_id }}</code>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="cs-td-note" :title="ticket.note">{{ ticket.note }}</td>
              <td>
                <span class="badge" :class="statusBadgeClass(ticket.status)">
                  {{ renameStatus(ticket.status) }}
                </span>
              </td>
              <td class="text-end pe-3">
                <div class="d-flex justify-content-end gap-1">
                  <button v-if="ticket.status === 'completed' && !ticket.invoiced"
                    title="Reabrir Ticket" class="btn btn-outline-primary btn-xs"
                    @click="$emit('re-open', ticket.id)">
                    <i class="fa fa-reply"></i>
                  </button>
                  <button v-if="ticket.status !== 'pending'"
                    title="Ver Atividades" class="btn btn-outline-success btn-xs"
                    @click="$emit('view-activities', ticket)">
                    <i class="fa fa-eye"></i>
                  </button>
                  <button v-if="ticket.status === 'pending'"
                    title="Editar" class="btn btn-outline-primary btn-xs"
                    @click="$emit('edit', ticket)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button v-if="ticket.status === 'pending'"
                    title="Excluir" class="btn btn-outline-danger btn-xs"
                    @click="$emit('delete', ticket.id)">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TicketTable',
  props: {
    tickets: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  methods: {
    formatDateBr(originalDate) {
      if (!originalDate) return '-'
      const data = new Date(originalDate)
      return data.toLocaleDateString("pt-BR")
    },
    statusBadgeClass(status) {
      return {
        'bg-secondary': status === 'pending',
        'bg-warning text-dark': status === 'in_progress',
        'bg-success': status === 'completed',
      }
    },
    renameStatus(status) {
      const statusMap = {
        'pending': 'Pendente',
        'in_progress': 'Em Andamento',
        'completed': 'Finalizado'
      }
      return statusMap[status] || status
    }
  }
}
</script>

<style scoped lang="sass">
.card
  border-radius: 20px
  border: none
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05)
  overflow: hidden
  background: white

.cs-table
  font-size: 0.88rem
  border-collapse: separate
  border-spacing: 0

  thead th
    background-color: #f8f9fa
    color: #a4b0be
    font-weight: 700
    text-transform: uppercase
    font-size: 0.65rem
    letter-spacing: 1.2px
    padding: 16px 12px
    border-bottom: 2px solid #f1f2f6

  tbody td
    vertical-align: middle
    padding: 16px 12px
    border-bottom: 1px solid #f1f2f6
    color: #2d3436
    transition: all 0.2s ease

  tbody tr
    transition: all 0.2s ease
    &:hover
      background-color: #fcfcfc
      td
        color: #000

  .cs-td-note
    max-width: 220px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    color: #636e72
    font-style: italic

.badge
  font-weight: 700
  font-size: 0.65rem
  padding: 6px 12px
  border-radius: 8px
  text-transform: uppercase
  letter-spacing: 0.5px
  
  &.bg-secondary
    background: #dfe6e9 !important
    color: #636e72 !important
  
  &.bg-warning
    background: #ffeaa7 !important
    color: #d6a317 !important
  
  &.bg-success
    background: #55efc4 !important
    color: #00b894 !important

.btn-xs
  width: 32px
  height: 32px
  border-radius: 8px
  display: flex
  align-items: center
  justify-content: center
  border: 2px solid transparent
  transition: all 0.2s ease
  background: transparent
  
  i
    font-size: 0.85rem

  &.btn-outline-primary
    color: #0984e3
    border-color: #e1f5fe
    &:hover
      background: #0984e3
      color: white
      border-color: #0984e3

  &.btn-outline-success
    color: #00b894
    border-color: #e0f2f1
    &:hover
      background: #00b894
      color: white
      border-color: #00b894

  &.btn-outline-danger
    color: #ff7675
    border-color: #ffebee
    &:hover
      background: #ff7675
      color: white
      border-color: #ff7675

code
  background: #f1f2f6
  padding: 4px 8px
  border-radius: 6px
  font-family: 'Fira Code', monospace
  font-size: 0.8rem
</style>
